import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  Card,
  List,
  Tag,
  Typography,
  Space,
  Avatar,
  Empty,
  Spin,
  Tabs,
  Button,
} from 'antd';
import {
  FileTextOutlined,
  ShopOutlined,
  UserOutlined,
  CreditCardOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import api from '../config/api';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
  buildWorkspaceNavigationState,
  workspaceTicketPath,
} from '../utils/workspaceNavigation';

dayjs.extend(relativeTime);

const { Text } = Typography;

const CCS_STATUS_LABELS: Record<string, string> = {
  new: 'New',
  in_progress: 'In Progress',
  completed: 'Solved',
  cancelled: 'Cancelled',
};

const REPLACEMENT_STATUS_LABELS: Record<string, string> = {
  new: 'New',
  in_progress: 'In Progress',
  completed: 'Completed',
};

const SEARCH_TAB_KEYS = new Set([
  'all',
  'tickets',
  'businesses',
  'credit_card_services',
  'replacements',
  'users',
]);

const formatUserName = (first?: string, last?: string) =>
  [first, last].filter(Boolean).join(' ') || '—';

const SearchResults: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get('q') || '';
  const tabFromUrl = searchParams.get('tab') || 'all';
  const [activeTab, setActiveTab] = useState(
    SEARCH_TAB_KEYS.has(tabFromUrl) ? tabFromUrl : 'all'
  );

  useEffect(() => {
    const nextTab = SEARCH_TAB_KEYS.has(tabFromUrl) ? tabFromUrl : 'all';
    setActiveTab(nextTab);
  }, [tabFromUrl]);

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    const next = new URLSearchParams(searchParams);
    if (!key || key === 'all') next.delete('tab');
    else next.set('tab', key);
    setSearchParams(next, { replace: false });
  };

  const openTicketFromSearch = (ticketNumber: string) => {
    navigate(workspaceTicketPath(ticketNumber), {
      state: buildWorkspaceNavigationState(
        {
          pathname: '/search',
          search: `?${searchParams.toString()}`,
        },
        { openInNewTab: true }
      ),
    });
  };

  const { data: searchResults, isLoading } = useQuery(
    ['global-search', query],
    () => {
      if (!query || query.trim().length === 0) {
        return {
          tickets: [],
          businesses: [],
          users: [],
          credit_card_services: [],
          replacements: [],
          total: 0,
        };
      }
      return api.get('/api/search', {
        params: { q: query, limit: 20 },
      }).then((res) => res.data);
    },
    {
      enabled: !!query && query.trim().length > 0,
      retry: false,
    }
  );

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      new: 'default',
      open: 'blue',
      pending: 'orange',
      solved: 'green',
      in_progress: 'blue',
      completed: 'green',
      cancelled: 'red',
    };
    return colors[status] || 'default';
  };

  const getPriorityColor = (priority: string) => {
    const colors: Record<string, string> = {
      low: 'blue',
      medium: 'orange',
      high: 'red',
      urgent: 'red',
    };
    return colors[priority] || 'default';
  };

  const tickets = searchResults?.tickets || [];
  const businesses = searchResults?.businesses || [];
  const users = searchResults?.users || [];
  const creditCardServices = searchResults?.credit_card_services || [];
  const replacements = searchResults?.replacements || [];
  const total = searchResults?.total || 0;

  const tabItems = [
    {
      key: 'all',
      label: `All (${total})`,
    },
    {
      key: 'tickets',
      label: `Tickets (${tickets.length})`,
    },
    {
      key: 'businesses',
      label: `Businesses (${businesses.length})`,
    },
    ...(creditCardServices.length > 0
      ? [{
          key: 'credit_card_services',
          label: `Credit Card Tasks (${creditCardServices.length})`,
        }]
      : []),
    ...(replacements.length > 0
      ? [{
          key: 'replacements',
          label: `Replacements (${replacements.length})`,
        }]
      : []),
    ...(users.length > 0
      ? [{
          key: 'users',
          label: `Users (${users.length})`,
        }]
      : []),
  ];

  const renderCreditCardServicesList = (items: any[], limit?: number) => (
    <List
      dataSource={limit ? items.slice(0, limit) : items}
      renderItem={(task: any) => (
        <List.Item
          style={{ cursor: 'pointer' }}
          onClick={() => navigate(`/credit-card-services?tab=tasks&taskId=${task.id}`)}
        >
          <List.Item.Meta
            avatar={<Avatar icon={<CreditCardOutlined />} style={{ backgroundColor: '#13c2c2' }} />}
            title={
              <Space>
                <Text strong>{task.service_type_label || task.service_type || 'Credit Card Task'}</Text>
                <Tag color={getStatusColor(task.status)}>
                  {CCS_STATUS_LABELS[task.status] || task.status}
                </Tag>
              </Space>
            }
            description={
              <Space wrap>
                {task.business_name && <Text type="secondary">{task.business_name}</Text>}
                {task.mid && <Text type="secondary">MID: {task.mid}</Text>}
                {(task.assigned_to_first_name || task.assigned_to_last_name) && (
                  <Text type="secondary">
                    Assigned to: {formatUserName(task.assigned_to_first_name, task.assigned_to_last_name)}
                  </Text>
                )}
                {task.created_at && <Text type="secondary">{dayjs(task.created_at).fromNow()}</Text>}
              </Space>
            }
          />
        </List.Item>
      )}
    />
  );

  const renderReplacementsList = (items: any[], limit?: number) => (
    <List
      dataSource={limit ? items.slice(0, limit) : items}
      renderItem={(replacement: any) => (
        <List.Item
          style={{ cursor: 'pointer' }}
          onClick={() => navigate(`/equipment?tab=replacements&replacementId=${replacement.id}`)}
        >
          <List.Item.Meta
            avatar={<Avatar icon={<SwapOutlined />} style={{ backgroundColor: '#fa8c16' }} />}
            title={
              <Space>
                <Text strong>
                  {[replacement.equipment_type, replacement.model].filter(Boolean).join(' – ') || 'Replacement'}
                </Text>
                <Tag color={getStatusColor(replacement.status)}>
                  {REPLACEMENT_STATUS_LABELS[replacement.status] || replacement.status}
                </Tag>
              </Space>
            }
            description={
              <Space wrap>
                {replacement.business_name && <Text type="secondary">{replacement.business_name}</Text>}
                {replacement.mid && <Text type="secondary">MID: {replacement.mid}</Text>}
                {replacement.ticket_number && <Text type="secondary">#{replacement.ticket_number}</Text>}
                {(replacement.replaced_by_first_name || replacement.replaced_by_last_name) && (
                  <Text type="secondary">
                    Assigned to: {formatUserName(replacement.replaced_by_first_name, replacement.replaced_by_last_name)}
                  </Text>
                )}
                {replacement.created_at && <Text type="secondary">{dayjs(replacement.created_at).fromNow()}</Text>}
              </Space>
            }
          />
        </List.Item>
      )}
    />
  );

  const renderContent = () => {
    if (activeTab === 'tickets') {
      if (tickets.length === 0) {
        return (
          <Empty
            description="No tickets found"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        );
      }
      return (
        <List
          dataSource={tickets}
          renderItem={(ticket: any) => (
            <List.Item
              style={{ cursor: 'pointer' }}
              onClick={() => openTicketFromSearch(ticket.ticket_number)}
            >
              <List.Item.Meta
                avatar={<Avatar icon={<FileTextOutlined />} style={{ backgroundColor: '#1890ff' }} />}
                title={
                  <Space>
                    <Text strong>#{ticket.ticket_number}</Text>
                    <Text>{ticket.subject}</Text>
                  </Space>
                }
                description={
                  <Space>
                    <Tag color={getStatusColor(ticket.status)}>{ticket.status}</Tag>
                    <Tag color={getPriorityColor(ticket.priority)}>{ticket.priority}</Tag>
                    {ticket.business_name && <Text type="secondary">{ticket.business_name}</Text>}
                    {ticket.assigned_to_name && (
                      <Text type="secondary">
                        Assigned to: {ticket.assigned_to_name} {ticket.assigned_to_last_name || ''}
                      </Text>
                    )}
                    <Text type="secondary">{dayjs(ticket.created_at).fromNow()}</Text>
                  </Space>
                }
              />
            </List.Item>
          )}
        />
      );
    }

    if (activeTab === 'businesses') {
      if (businesses.length === 0) {
        return (
          <Empty
            description="No businesses found"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        );
      }
      return (
        <List
          dataSource={businesses}
          renderItem={(business: any) => (
            <List.Item
              style={{ cursor: 'pointer' }}
              onClick={() => navigate(`/businesses/${business.id}`)}
            >
              <List.Item.Meta
                avatar={<Avatar icon={<ShopOutlined />} style={{ backgroundColor: '#52c41a' }} />}
                title={<Text strong>{business.business_name}</Text>}
                description={
                  <Space direction="vertical" size="small">
                    {business.mid && <Text type="secondary">MID: {business.mid}</Text>}
                    {business.email && <Text type="secondary">Email: {business.email}</Text>}
                    {business.phone && <Text type="secondary">Phone: {business.phone}</Text>}
                    {(business.address || business.city || business.state || business.zip_code) && (
                      <Text type="secondary">
                        {[
                          business.address,
                          [business.city, business.state].filter(Boolean).join(', '),
                          business.zip_code,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </Text>
                    )}
                  </Space>
                }
              />
            </List.Item>
          )}
        />
      );
    }

    if (activeTab === 'credit_card_services') {
      if (creditCardServices.length === 0) {
        return (
          <Empty
            description="No credit card service tasks found"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        );
      }
      return renderCreditCardServicesList(creditCardServices);
    }

    if (activeTab === 'replacements') {
      if (replacements.length === 0) {
        return (
          <Empty
            description="No replacement tasks found"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        );
      }
      return renderReplacementsList(replacements);
    }

    if (activeTab === 'users') {
      if (users.length === 0) {
        return (
          <Empty
            description="No users found"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        );
      }
      return (
        <List
          dataSource={users}
          renderItem={(user: any) => (
            <List.Item
              style={{ cursor: 'pointer' }}
              onClick={() => navigate(`/users`)}
            >
              <List.Item.Meta
                avatar={
                  <Avatar icon={<UserOutlined />} style={{ backgroundColor: '#722ed1' }}>
                    {user.first_name?.[0]}{user.last_name?.[0]}
                  </Avatar>
                }
                title={
                  <Space>
                    <Text strong>{user.first_name} {user.last_name}</Text>
                    {user.role_display_name && (
                      <Tag>{user.role_display_name}</Tag>
                    )}
                    {!user.is_active && <Tag color="red">Inactive</Tag>}
                  </Space>
                }
                description={
                  <Space direction="vertical" size="small">
                    {user.email && <Text type="secondary">Email: {user.email}</Text>}
                    {user.phone && <Text type="secondary">Phone: {user.phone}</Text>}
                  </Space>
                }
              />
            </List.Item>
          )}
        />
      );
    }

    // All tab
    if (total === 0) {
      return (
        <Empty
          description={query ? 'No results found' : 'Enter a search term to begin'}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      );
    }

    return (
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {tickets.length > 0 && (
          <Card
            title={
              <Space>
                <FileTextOutlined />
                <Text strong>Tickets ({tickets.length})</Text>
              </Space>
            }
            size="small"
          >
            <List
              dataSource={tickets.slice(0, 5)}
              renderItem={(ticket: any) => (
                <List.Item
                  style={{ cursor: 'pointer' }}
                  onClick={() => openTicketFromSearch(ticket.ticket_number)}
                >
                  <List.Item.Meta
                    avatar={<Avatar icon={<FileTextOutlined />} style={{ backgroundColor: '#1890ff' }} />}
                    title={
                      <Space>
                        <Text strong>#{ticket.ticket_number}</Text>
                        <Text>{ticket.subject}</Text>
                      </Space>
                    }
                    description={
                      <Space>
                        <Tag color={getStatusColor(ticket.status)}>{ticket.status}</Tag>
                        <Tag color={getPriorityColor(ticket.priority)}>{ticket.priority}</Tag>
                        {ticket.business_name && <Text type="secondary">{ticket.business_name}</Text>}
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
            {tickets.length > 5 && (
              <div style={{ textAlign: 'center', marginTop: 16 }}>
                <Button type="link" onClick={() => handleTabChange('tickets')}>
                  View all {tickets.length} tickets
                </Button>
              </div>
            )}
          </Card>
        )}

        {businesses.length > 0 && (
          <Card
            title={
              <Space>
                <ShopOutlined />
                <Text strong>Businesses ({businesses.length})</Text>
              </Space>
            }
            size="small"
          >
            <List
              dataSource={businesses.slice(0, 5)}
              renderItem={(business: any) => (
                <List.Item
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/businesses/${business.id}`)}
                >
                  <List.Item.Meta
                    avatar={<Avatar icon={<ShopOutlined />} style={{ backgroundColor: '#52c41a' }} />}
                    title={<Text strong>{business.business_name}</Text>}
                    description={
                      <Space direction="vertical" size="small">
                        {business.mid && <Text type="secondary">MID: {business.mid}</Text>}
                        {business.email && <Text type="secondary">Email: {business.email}</Text>}
                        {business.phone && <Text type="secondary">Phone: {business.phone}</Text>}
                        {(business.address || business.city || business.state || business.zip_code) && (
                          <Text type="secondary">
                            {[
                              business.address,
                              [business.city, business.state].filter(Boolean).join(', '),
                              business.zip_code,
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </Text>
                        )}
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
            {businesses.length > 5 && (
              <div style={{ textAlign: 'center', marginTop: 16 }}>
                <Button type="link" onClick={() => handleTabChange('businesses')}>
                  View all {businesses.length} businesses
                </Button>
              </div>
            )}
          </Card>
        )}

        {creditCardServices.length > 0 && (
          <Card
            title={
              <Space>
                <CreditCardOutlined />
                <Text strong>Credit Card Tasks ({creditCardServices.length})</Text>
              </Space>
            }
            size="small"
          >
            {renderCreditCardServicesList(creditCardServices, 5)}
            {creditCardServices.length > 5 && (
              <div style={{ textAlign: 'center', marginTop: 16 }}>
                <Button type="link" onClick={() => handleTabChange('credit_card_services')}>
                  View all {creditCardServices.length} credit card tasks
                </Button>
              </div>
            )}
          </Card>
        )}

        {replacements.length > 0 && (
          <Card
            title={
              <Space>
                <SwapOutlined />
                <Text strong>Replacements ({replacements.length})</Text>
              </Space>
            }
            size="small"
          >
            {renderReplacementsList(replacements, 5)}
            {replacements.length > 5 && (
              <div style={{ textAlign: 'center', marginTop: 16 }}>
                <Button type="link" onClick={() => handleTabChange('replacements')}>
                  View all {replacements.length} replacements
                </Button>
              </div>
            )}
          </Card>
        )}

        {users.length > 0 && (
          <Card
            title={
              <Space>
                <UserOutlined />
                <Text strong>Users ({users.length})</Text>
              </Space>
            }
            size="small"
          >
            <List
              dataSource={users.slice(0, 5)}
              renderItem={(user: any) => (
                <List.Item
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/users`)}
                >
                  <List.Item.Meta
                    avatar={
                      <Avatar icon={<UserOutlined />} style={{ backgroundColor: '#722ed1' }}>
                        {user.first_name?.[0]}{user.last_name?.[0]}
                      </Avatar>
                    }
                    title={
                      <Space>
                        <Text strong>{user.first_name} {user.last_name}</Text>
                        {user.role_display_name && <Tag>{user.role_display_name}</Tag>}
                      </Space>
                    }
                    description={
                      <Text type="secondary">{user.email}</Text>
                    }
                  />
                </List.Item>
              )}
            />
            {users.length > 5 && (
              <div style={{ textAlign: 'center', marginTop: 16 }}>
                <Button type="link" onClick={() => handleTabChange('users')}>
                  View all {users.length} users
                </Button>
              </div>
            )}
          </Card>
        )}
      </Space>
    );
  };

  return (
    <div style={{ padding: 24, background: '#f0f2f5', minHeight: '100vh' }}>
      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {query && (
            <>
              <div>
                <Text type="secondary">
                  {isLoading ? 'Searching...' : `Found ${total} result${total !== 1 ? 's' : ''} for "${query}"`}
                </Text>
              </div>

              {isLoading ? (
                <div style={{ textAlign: 'center', padding: 40 }}>
                  <Spin size="large" />
                </div>
              ) : (
                <>
                  {total > 0 && (
                    <Tabs
                      activeKey={activeTab}
                      onChange={handleTabChange}
                      items={tabItems}
                    />
                  )}
                  {renderContent()}
                </>
              )}
            </>
          )}

          {!query && (
            <Empty
              description="Enter a search term in the header to find tickets, businesses, credit card tasks, replacements, or users"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          )}
        </Space>
      </Card>
    </div>
  );
};

export default SearchResults;
